import React, { useState, useEffect } from "react";
import {
  Calendar,
  Plus,
  ChevronLeft,
  ChevronRight,
  Edit2,
  Trash2,
  Clock,
  Info,
  Bell,
  CheckCircle2,
  FileText,
  Search,
  X,
  Pill,
} from "lucide-react";
import {
  Modal,
  Button,
  DatePicker,
  Input,
  Radio,
  message,
  Card,
  Tag,
  Empty,
  Spin,
  Tooltip,
  Table,
  Checkbox,
} from "antd";
import dayjs from "dayjs";
import MedicineReminderService from "../../Services/MedicineReminderService";
import DrugServices from "../../Services/DrugServices";
import AuthService from "../../Services/AuthService";
import { useSelector } from "react-redux";
import Navbar from "../../DashBoardComponents/Navbar";

const { TextArea } = Input;

const daysOfWeek = [
  { key: "monday", label: "Mon" },
  { key: "tuesday", label: "Tue" },
  { key: "wednesday", label: "Wed" },
  { key: "thursday", label: "Thu" },
  { key: "friday", label: "Fri" },
  { key: "saturday", label: "Sat" },
  { key: "sunday", label: "Sun" },
];

const MedicineReminder = () => {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [baseDate, setBaseDate] = useState(dayjs()); // State to control the sliding window of dates

  // Medicine Search State
  const [medicines, setMedicines] = useState([]);
  const [searchingMedicines, setSearchingMedicines] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Prescription State
  const [isPrescModalOpen, setIsPrescModalOpen] = useState(false);
  const [prescriptions, setPrescriptions] = useState([]);
  const [loadingPresc, setLoadingPresc] = useState(false);

  const { loggedUser } = useSelector((state) => state.auth);
  const patientId = loggedUser?.patientId || JSON.parse(localStorage.getItem("patientData"))?.patientId;

  // Form State
  const [form, setForm] = useState({
    drugName: "",
    morningAppl: "N",
    morningTime: "08:00",
    afternoonAppl: "N",
    afternoonTime: "13:00",
    eveningAppl: "N",
    eveningTime: "18:00",
    notificationFromDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
    notificationToDate: dayjs().add(7, "day").format("YYYY-MM-DD HH:mm:ss"),
    whenToTake: "After Food",
    instruction: "",
    monday: "N",
    tuesday: "N",
    wednesday: "N",
    thursday: "N",
    friday: "N",
    saturday: "N",
    sunday: "N",
  });

  const [medicationList, setMedicationList] = useState([]);
  const [currentEditIndex, setCurrentEditIndex] = useState(null);
  const [prescriptionId, setPrescriptionId] = useState("");
  const [dosagePattern, setDosagePattern] = useState("0-0-0");

  useEffect(() => {
    fetchReminders();
  }, [selectedDate, patientId]);

  const fetchReminders = async () => {
    if (!patientId) return;
    setLoading(true);
    try {
      const payload = {
        params: {
          patientId: patientId,
          fromDate: selectedDate.format("MM/DD/YYYY"),
          toDate: selectedDate.format("MM/DD/YYYY"),
        }
      };
      const response = await MedicineReminderService.listReminders(payload);
      setReminders(response.data || []);
    } catch (error) {
      console.error("Error fetching reminders:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearchMedicines = async (value) => {
    setForm({ ...form, drugName: value });
    if (value.length < 2) {
      setMedicines([]);
      setShowSuggestions(false);
      return;
    }
    setSearchingMedicines(true);
    try {
      const response = await DrugServices.fetchDrugList(value, 1, 20);
      setMedicines(response.data.data || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error("Error searching medicines:", error);
    } finally {
      setSearchingMedicines(false);
    }
  };

  const selectMedicine = (medName) => {
    setForm({ ...form, drugName: medName });
    setShowSuggestions(false);
    setMedicines([]);
  };

  const fetchPrescriptions = async () => {
    if (!patientId) {
      message.warning("Patient identification missing. Please login again.");
      return;
    }
    setLoadingPresc(true);
    setIsPrescModalOpen(true);
    try {
      let payload = {
        patientId: patientId,
        detailsYN: "Y",
        status: 2,
        pageNo: 1,
        pageSize: 50,
      };
      const response = await AuthService.getMyPrescriptionView(payload);
      setPrescriptions(response.data || []);
    } catch (error) {
      console.error("Error fetching prescriptions:", error);
      message.error("Failed to load prescriptions");
    } finally {
      setLoadingPresc(false);
    }
  };

  const handleSelectPrescription = (presc) => {
    setPrescriptionId(presc.consultationId || presc.id || "");
    const meds = presc.drugPrescription?.map(med => {
      const dosageParts = med.dosage ? med.dosage.split("-").map(s => s.trim()) : ["0", "0", "0"];
      return {
        drugName: med.drugName || med.medicineName,
        instruction: med.instruction || med.notes || "",
        whenToTake: med.afterBeforeFood === "AF" ? "After Food" : "Before Food",
        morningAppl: dosageParts[0] === "1" ? "Y" : "N",
        afternoonAppl: dosageParts[1] === "1" ? "Y" : "N",
        eveningAppl: dosageParts[2] === "1" ? "Y" : "N",
        morningTime: "08:00",
        afternoonTime: "13:00",
        eveningTime: "18:00",
        notificationFromDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
        notificationToDate: dayjs().add(7, "day").format("YYYY-MM-DD HH:mm:ss"),
        monday: "N",
        tuesday: "N",
        wednesday: "N",
        thursday: "N",
        friday: "N",
        saturday: "N",
        sunday: "N",
      };
    }) || [];

    setMedicationList(meds);
    setIsPrescModalOpen(false);
    setIsModalOpen(true);
  };

  const handleDosageChange = (pattern) => {
    setDosagePattern(pattern);
    const [m, a, e] = pattern.split("-");
    setForm({
      ...form,
      morningAppl: m === "1" ? "Y" : "N",
      afternoonAppl: a === "1" ? "Y" : "N",
      eveningAppl: e === "1" ? "Y" : "N",
    });
  };

  const addMedicineToList = () => {
    if (!form.drugName) {
      message.error("Please enter a medicine name");
      return;
    }

    if (currentEditIndex !== null) {
      const newList = [...medicationList];
      newList[currentEditIndex] = { ...form };
      setMedicationList(newList);
      setCurrentEditIndex(null);
      message.success("Medicine updated in list");
    } else {
      setMedicationList([...medicationList, { ...form }]);
      message.success("Medicine added to list");
    }

    // Clear form for next entry but keep common fields
    setForm({
      ...form,
      drugName: "",
      morningAppl: "N",
      afternoonAppl: "N",
      eveningAppl: "N",
      instruction: "",
    });
    setDosagePattern("0-0-0");
  };

  const handleSave = async () => {
    if (!editingReminder && medicationList.length === 0) {
      message.error("Please add at least one medicine to the list");
      return;
    }
    if (editingReminder && !form.drugName) {
      message.error("Please enter a medicine name");
      return;
    }

    setLoading(true);
    try {
      // If we are editing a single reminder (not part of the multi-add flow)
      if (editingReminder) {
        const payload = {
          ...form,
          patientId: patientId,
          prescriptionId: prescriptionId || null,
          modifiedBy: patientId,
          modifiedDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          status: 1,
          morningTime: form.morningAppl === "Y" ? (form.morningTime ? form.morningTime.substring(0, 5) : "08:00") : null,
          afternoonTime: form.afternoonAppl === "Y" ? (form.afternoonTime ? form.afternoonTime.substring(0, 5) : "13:00") : null,
          eveningTime: form.eveningAppl === "Y" ? (form.eveningTime ? form.eveningTime.substring(0, 5) : "18:00") : null,
          monday: form.monday || "N",
          tuesday: form.tuesday || "N",
          wednesday: form.wednesday || "N",
          thursday: form.thursday || "N",
          friday: form.friday || "N",
          saturday: form.saturday || "N",
          sunday: form.sunday || "N",
        };
        await MedicineReminderService.updateReminder([payload]);
        message.success("Reminder updated successfully");
      } else {
        // Saving multiple reminders in a single batch call
        const batchPayload = medicationList.map(med => ({
          ...med,
          patientId: patientId,
          prescriptionId: prescriptionId || null,
          createdBy: patientId,
          modifiedBy: patientId,
          createdDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          modifiedDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          currentDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
          status: 1,
          morningTime: med.morningAppl === "Y" ? (med.morningTime ? med.morningTime.substring(0, 5) : "08:00") : null,
          afternoonTime: med.afternoonAppl === "Y" ? (med.afternoonTime ? med.afternoonTime.substring(0, 5) : "13:00") : null,
          eveningTime: med.eveningAppl === "Y" ? (med.eveningTime ? med.eveningTime.substring(0, 5) : "18:00") : null,
          monday: med.monday || "N",
          tuesday: med.tuesday || "N",
          wednesday: med.wednesday || "N",
          thursday: med.thursday || "N",
          friday: med.friday || "N",
          saturday: med.saturday || "N",
          sunday: med.sunday || "N",
        }));
        await MedicineReminderService.saveReminder(batchPayload);
        message.success(`${medicationList.length} reminders saved successfully`);
      }
      setIsModalOpen(false);
      fetchReminders();
      resetForm();
      setMedicationList([]);
    } catch (error) {
      console.error("Error saving reminders:", error);
      message.error(editingReminder ? "Failed to update reminder" : "Failed to save reminders");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      drugName: "",
      morningAppl: "N",
      morningTime: "08:00",
      afternoonAppl: "N",
      afternoonTime: "13:00",
      eveningAppl: "N",
      eveningTime: "18:00",
      notificationFromDate: dayjs().format("YYYY-MM-DD HH:mm:ss"),
      notificationToDate: dayjs().add(7, "day").format("YYYY-MM-DD HH:mm:ss"),
      whenToTake: "After Food",
      instruction: "",
      monday: "N",
      tuesday: "N",
      wednesday: "N",
      thursday: "N",
      friday: "N",
      saturday: "N",
      sunday: "N",
    });
    setEditingReminder(null);
    setMedicationList([]);
    setCurrentEditIndex(null);
    setDosagePattern("0-0-0");
    setPrescriptionId("");
  };

  const editReminder = (reminder) => {
    setEditingReminder(reminder);
    setForm({
      ...reminder,
      notificationFromDate: dayjs(reminder.notificationFromDate).format("YYYY-MM-DD HH:mm:ss"),
      notificationToDate: dayjs(reminder.notificationToDate).format("YYYY-MM-DD HH:mm:ss"),
      morningTime: reminder.morningTime?.substring(0, 5) || "08:00",
      afternoonTime: reminder.afternoonTime?.substring(0, 5) || "13:00",
      eveningTime: reminder.eveningTime?.substring(0, 5) || "18:00",
    });
    const pattern = `${reminder.morningAppl === "Y" ? "1" : "0"}-${reminder.afternoonAppl === "Y" ? "1" : "0"}-${reminder.eveningAppl === "Y" ? "1" : "0"}`;
    setDosagePattern(pattern);
    setMedicationList([]);
    setIsModalOpen(true);
  };

  const renderDateStrip = () => {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      dates.push(baseDate.add(i, 'day'));
    }

    return (
      <div className="flex items-center justify-between gap-2 overflow-x-auto pb-4 hide-scrollbar">
        {dates.map((date, idx) => {
          const isSelected = date.isSame(selectedDate, "day");
          return (
            <div
              key={idx}
              onClick={() => setSelectedDate(date)}
              className={`flex flex-col items-center justify-center min-w-[70px] h-[90px] rounded-2xl cursor-pointer transition-all duration-300 ${isSelected
                ? "bg-[#189FAA] text-white shadow-lg scale-105"
                : "bg-white text-gray-500 border border-gray-100 hover:border-[#189FAA]"
                }`}
            >
              <span className="text-xs font-medium uppercase">{date.format("ddd")}</span>
              <span className="text-2xl font-bold mt-1">{date.format("DD")}</span>
              <span className="text-[10px] uppercase">{date.format("MMM")}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f8fafc] pb-20 pt-[90px]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-white rounded-2xl shadow-sm">
              <Bell className="text-[#189FAA]" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-[#071a3d]">Medicine Reminder</h1>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={fetchPrescriptions}
              className="h-12 px-6 rounded-xl border-[#189FAA] text-[#189FAA] font-semibold hover:bg-[#189FAA]/5 flex items-center gap-2"
            >
              <FileText size={18} /> Select Prescription
            </Button>
            <Button
              type="primary"
              icon={<Plus size={18} />}
              onClick={() => { resetForm(); setIsModalOpen(true); }}
              className="h-12 px-6 rounded-xl bg-[#189FAA] border-none shadow-md hover:scale-105 transition-transform font-semibold text-white"
            >
              Add Schedule
            </Button>
          </div>
        </div>

        {/* Date Section */}
        <div className="bg-white p-6 rounded-[30px] shadow-sm mb-6 border border-gray-50">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-bold text-gray-800">Select Date</h3>
            <div className="flex gap-2">
              <button onClick={() => setBaseDate(baseDate.subtract(1, 'day'))} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ChevronLeft size={20} className="text-gray-400" />
              </button>
              <button onClick={() => setBaseDate(baseDate.add(1, 'day'))} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <ChevronRight size={20} className="text-gray-400" />
              </button>
            </div>
          </div>
          {renderDateStrip()}
        </div>

        {/* Medicines List */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-gray-800 text-lg">Medicines</h3>
          </div>

          {loading ? (
            <div className="flex justify-center p-20">
              <Spin size="large" />
            </div>
          ) : (() => {
            const currentDayName = selectedDate.format("dddd").toLowerCase();
            const filteredReminders = reminders.filter((item) => item[currentDayName] === "Y");

            if (filteredReminders.length === 0) {
              return (
                <div className="bg-white p-12 rounded-[30px] border border-dashed border-gray-200 text-center">
                  <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Bell className="text-gray-300" size={32} />
                  </div>
                  <h4 className="text-gray-400 font-medium">No medicines scheduled for today</h4>
                  <p className="text-gray-300 text-sm mt-1">Select a prescription or add a medicine to build the schedule.</p>
                </div>
              );
            }

            const morningMeds = filteredReminders.filter((item) => item.morningAppl === "Y");
            const afternoonMeds = filteredReminders.filter((item) => item.afternoonAppl === "Y");
            const eveningMeds = filteredReminders.filter((item) => item.eveningAppl === "Y");

            const renderMedicineCard = (item, idx) => (
              <Card key={`${item.id || idx}-${Math.random()}`} className="rounded-[24px] border-none shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex gap-4">
                    <div className="w-12 h-12 bg-[#189FAA]/10 rounded-2xl flex items-center justify-center">
                      <Pill color="#189FAA" size={24} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="text-lg font-bold text-[#071a3d]">{item.drugName}</h4>
                        <Tag color="blue" className="rounded-md border-none px-2 py-0.5 text-[10px] font-bold">TABLET</Tag>
                      </div>
                      <Tag color="green" className="bg-green-50 text-green-600 border-none rounded-lg px-3 py-1 font-semibold text-xs mb-3">{item.whenToTake || 'After Food'}</Tag>

                      {item?.instruction && <div className="flex items-start gap-2 mt-2">
                        <Info size={16} className="text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-xs font-bold text-gray-500 uppercase">Instruction</p>
                          <p className="text-sm text-gray-400 whitespace-pre-line">{item.instruction || ""}</p>
                        </div>
                      </div>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-3 shrink-0">
                    <Button
                      icon={<Edit2 size={16} />}
                      onClick={() => editReminder(item)}
                      className="flex items-center gap-1 font-semibold border-none text-[#189FAA] bg-transparent"
                    >
                      Edit
                    </Button>
                    <div className="flex flex-col items-center bg-gray-50 px-4 py-2 rounded-2xl border border-gray-100 min-w-[100px]">
                      <span className="text-[10px] font-bold text-gray-400 uppercase">1 Tablet</span>
                      <span className="text-sm font-bold text-gray-700">Frequency</span>
                      <div className="mt-1 font-bold text-[#189FAA]">
                        {item.morningAppl === "Y" ? "1" : "0"}-{item.afternoonAppl === "Y" ? "1" : "0"}-{item.eveningAppl === "Y" ? "1" : "0"}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            );

            return (
              <div className="space-y-6">
                {morningMeds.length > 0 && (
                  <div>
                    <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">Morning <Tag color="blue" className="rounded-full border-none">{morningMeds.length}</Tag></h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                       {morningMeds.map(renderMedicineCard)}
                    </div>
                  </div>
                )}
                {afternoonMeds.length > 0 && (
                  <div>
                    <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">Afternoon <Tag color="orange" className="rounded-full border-none">{afternoonMeds.length}</Tag></h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                       {afternoonMeds.map(renderMedicineCard)}
                    </div>
                  </div>
                )}
                {eveningMeds.length > 0 && (
                  <div>
                    <h4 className="font-bold text-gray-700 mb-3 flex items-center gap-2">Evening <Tag color="purple" className="rounded-full border-none">{eveningMeds.length}</Tag></h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                       {eveningMeds.map(renderMedicineCard)}
                    </div>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        title={null}
        open={isModalOpen}
        onCancel={() => { setIsModalOpen(false); setMedicationList([]); }}
        footer={null}
        width={950}
        centered
        className="medicine-modal"
        styles={{
          mask: { backdropFilter: 'blur(4px)' },
          content: { borderRadius: '28px', padding: 0, overflow: 'visible' }
        }}
      >
        <div className="p-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-2xl font-bold text-[#071a3d]">{editingReminder ? 'Edit Medicine' : 'Add Medicine Schedule'}</h2>
            {/* <button onClick={() => { setIsModalOpen(false); setMedicationList([]); }} className="w-10 h-10 flex items-center justify-center bg-gray-50 rounded-full hover:bg-gray-100 transition-colors">
              <X size={20} className="text-gray-400" />
            </button> */}
          </div>

          <div className="flex flex-col lg:flex-row gap-8">
            {/* Form Section */}
            <div className="flex-1 space-y-6 max-h-[600px] overflow-y-auto pr-4 custom-scrollbar">
              <div className="relative">
                <label className="text-xs font-bold text-gray-400 uppercase mb-2 block">Medicine Name</label>
                <div className="relative">
                  <Input
                    placeholder="Type medicine name"
                    className="w-full h-12 rounded-xl border-gray-100 pr-10 font-semibold"
                    value={form.drugName}
                    onChange={(e) => handleSearchMedicines(e.target.value)}
                    onFocus={() => form.drugName.length >= 2 && setShowSuggestions(true)}
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {searchingMedicines ? <Spin size="small" /> : <Search size={18} className="text-gray-300" />}
                  </div>
                </div>

                {showSuggestions && (
                  <div className="absolute z-[1001] left-0 right-0 mt-1 bg-white border border-gray-100 rounded-2xl shadow-xl max-h-[200px] overflow-y-auto overflow-x-hidden p-2">
                    {medicines.length > 0 ? (
                      <ul className="m-0 p-0 list-none">
                        {medicines.map((med, idx) => (
                          <li
                            key={idx}
                            onClick={() => selectMedicine(med.medicineName || med.drugName)}
                            className="px-4 py-3 hover:bg-gray-50 rounded-xl cursor-pointer transition-colors flex items-center gap-3 border-b border-gray-50 last:border-none"
                          >
                            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                              <Pill size={16} color="#189FAA" />
                            </div>
                            <div>
                              <div className="font-bold text-[#071a3d] leading-tight">{med.medicineName}</div>
                              <div className="text-[10px] text-gray-400 uppercase font-medium">{med.drugCategory || 'General'}</div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="px-4 py-6 text-center">
                        <div className="text-gray-400 text-sm font-medium mb-1">Medicine not found</div>
                        <div className="text-gray-300 text-xs">Press Add to List to use "{form.drugName}"</div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase mb-2 block">From Date</label>
                  <DatePicker
                    value={form.notificationFromDate ? dayjs(form.notificationFromDate) : null}
                    onChange={(date) => setForm({ ...form, notificationFromDate: date ? date.format("YYYY-MM-DD HH:mm:ss") : "" })}
                    className="w-full h-12 rounded-xl border-gray-100"
                    format="DD/MM/YYYY"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-400 uppercase mb-2 block">To Date</label>
                  <DatePicker
                    value={form.notificationToDate ? dayjs(form.notificationToDate) : null}
                    onChange={(date) => setForm({ ...form, notificationToDate: date ? date.format("YYYY-MM-DD HH:mm:ss") : "" })}
                    className="w-full h-12 rounded-xl border-gray-100"
                    format="DD/MM/YYYY"
                  />
                </div>
              </div>

              <div>
                <div className="flex items-center gap-1 mb-2">
                  <label className="text-xs font-bold text-gray-400 uppercase block">Dosage (Frequency)</label>
                  <Tooltip title="Morning - Afternoon - Evening">
                    <Info size={14} className="text-gray-300" />
                  </Tooltip>
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {["1-1-1", "1-0-1", "1-0-0", "0-1-0", "0-0-1"].map((p) => (
                    <button
                      key={p}
                      onClick={() => handleDosageChange(p)}
                      className={`h-10 rounded-xl border text-[11px] font-bold transition-all ${dosagePattern === p
                        ? "bg-[#189FAA]/10 border-[#189FAA] text-[#189FAA]"
                        : "bg-white border-gray-100 text-gray-600 hover:border-gray-300"
                        }`}
                    >
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-400 uppercase mb-2 block">When to Take</label>
                <div className="flex gap-4">
                  {["After Food", "Before Food"].map((opt) => (
                    <button
                      key={opt}
                      onClick={() => setForm({ ...form, whenToTake: opt })}
                      className={`flex-1 h-12 rounded-xl border flex items-center justify-center gap-2 transition-all ${form.whenToTake === opt
                        ? "bg-[#189FAA]/10 border-[#189FAA] text-[#189FAA]"
                        : "bg-white border-gray-100 text-gray-600"
                        }`}
                    >
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${form.whenToTake === opt ? "border-[#189FAA]" : "border-gray-200"}`}>
                        {form.whenToTake === opt && <div className="w-2.5 h-2.5 bg-[#189FAA] rounded-full" />}
                      </div>
                      <span className="font-bold">{opt}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-400 uppercase mb-2 block">Repeat Days</label>
                <div className="flex items-center gap-4 flex-wrap bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <Checkbox
                    checked={
                      form.monday === "Y" &&
                      form.tuesday === "Y" &&
                      form.wednesday === "Y" &&
                      form.thursday === "Y" &&
                      form.friday === "Y" &&
                      form.saturday === "Y" &&
                      form.sunday === "Y"
                    }
                    onChange={(e) => {
                      const val = e.target.checked ? "Y" : "N";
                      setForm({
                        ...form,
                        monday: val,
                        tuesday: val,
                        wednesday: val,
                        thursday: val,
                        friday: val,
                        saturday: val,
                        sunday: val,
                      });
                    }}
                    className="font-bold text-[#071a3d]"
                  >
                    All
                  </Checkbox>
                  {daysOfWeek.map((day) => (
                    <Checkbox
                      key={day.key}
                      checked={form[day.key] === "Y"}
                      onChange={(e) => {
                        setForm({
                          ...form,
                          [day.key]: e.target.checked ? "Y" : "N",
                        });
                      }}
                      className="font-semibold text-gray-600"
                    >
                      {day.label}
                    </Checkbox>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold text-gray-400 uppercase mb-2 block">Instruction</label>
                <TextArea
                  rows={2}
                  placeholder="Enter instruction"
                  value={form.instruction}
                  onChange={(e) => setForm({ ...form, instruction: e.target.value })}
                  className="rounded-xl border-gray-100 p-3 font-medium"
                  maxLength={100}
                />
              </div>

              <Button
                type="dashed"
                block
                size="large"
                onClick={addMedicineToList}
                className="h-12 rounded-xl border-[#189FAA] text-[#189FAA] font-bold hover:bg-[#189FAA]/5"
              >
                {currentEditIndex !== null ? 'Update in List' : 'Add to List'}
              </Button>
            </div>

            {/* List Section */}
            {!editingReminder && <div className="flex-1 bg-gray-50 rounded-2xl p-4 flex flex-col border border-gray-100">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-[#071a3d]">Added Medicines ({medicationList.length})</h3>
              </div>

              <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
                {medicationList.length > 0 ? (
                  <div className="space-y-3">
                    {medicationList.map((med, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-xl shadow-sm border border-gray-100 group relative">
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-bold text-sm text-[#071a3d] leading-none">{med.drugName}</div>
                            <div className="text-[10px] text-gray-400 font-bold uppercase mt-1.5 bg-gray-50 inline-block px-1.5 py-0.5 rounded">
                              {med.morningAppl === 'Y' ? '1' : '0'}-{med.afternoonAppl === 'Y' ? '1' : '0'}-{med.eveningAppl === 'Y' ? '1' : '0'}
                              {" • "}{med.whenToTake}
                              {" • "}
                              {
                                med.monday === "Y" && med.tuesday === "Y" && med.wednesday === "Y" && med.thursday === "Y" && med.friday === "Y" && med.saturday === "Y" && med.sunday === "Y"
                                  ? "Daily"
                                  : [
                                    med.monday === "Y" && "Mon",
                                    med.tuesday === "Y" && "Tue",
                                    med.wednesday === "Y" && "Wed",
                                    med.thursday === "Y" && "Thu",
                                    med.friday === "Y" && "Fri",
                                    med.saturday === "Y" && "Sat",
                                    med.sunday === "Y" && "Sun"
                                  ].filter(Boolean).join(", ") || "No Days"
                              }
                            </div>
                          </div>
                          <div className="flex opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button type="text" size="small" icon={<Edit2 size={14} className="text-[#189FAA]" />} onClick={() => { setForm(med); setCurrentEditIndex(idx); setDosagePattern(`${med.morningAppl === 'Y' ? '1' : '0'}-${med.afternoonAppl === 'Y' ? '1' : '0'}-${med.eveningAppl === 'Y' ? '1' : '0'}`); }} />
                            <Button type="text" size="small" danger icon={<Trash2 size={14} />} onClick={() => setMedicationList(medicationList.filter((_, i) => i !== idx))} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-300">
                    <Pill size={40} color="#e5e7eb" />
                    <p className="text-sm mt-2">No medicines added yet</p>
                  </div>
                )}
              </div>

              <div className="mt-4 pt-4 border-t border-gray-200">
                <Button
                  type="primary"
                  block
                  size="large"
                  onClick={handleSave}
                  loading={loading}
                  disabled={medicationList.length === 0}
                  className="h-14 rounded-2xl bg-[#189FAA] border-none font-bold shadow-lg text-white"
                >
                  Save All Reminders {medicationList.length > 0 && `(${medicationList.length})`}
                </Button>
              </div>
            </div>}

            {editingReminder && <div className="flex-1 flex flex-col justify-end">
              <div className="flex gap-4">
                <Button block onClick={() => setIsModalOpen(false)} className="h-14 rounded-2xl font-bold">Cancel</Button>
                <Button type="primary" block onClick={handleSave} loading={loading} className="h-14 rounded-2xl bg-[#189FAA] border-none font-bold text-white">Update Reminder</Button>
              </div>
            </div>}
          </div>
        </div>
      </Modal>

      {/* Prescription Selection Modal */}
      <Modal
        title={null}
        open={isPrescModalOpen}
        onCancel={() => {
          setIsPrescModalOpen(false);
        }}
        footer={null}
        width={500}
        centered
        styles={{
          mask: { backdropFilter: 'blur(4px)' },
          content: { borderRadius: '28px', padding: 0, overflow: 'hidden' }
        }}
      >
        <div className="p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-blue-50 rounded-xl">
              <FileText className="text-[#189FAA]" size={20} />
            </div>
            <h2 className="text-xl font-bold text-[#071a3d]">Select Prescription</h2>
          </div>

          {loadingPresc ? (
            <div className="py-20 flex justify-center"><Spin /></div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
              {prescriptions.length > 0 ? (
                prescriptions.map((presc, idx) => (
                  <div
                    key={idx}
                    onClick={() => handleSelectPrescription(presc)}
                    className="p-4 bg-white border border-gray-100 rounded-2xl hover:border-[#189FAA] hover:shadow-md transition-all cursor-pointer group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex gap-4">
                        <div className="w-12 h-12 bg-gray-50 rounded-2xl flex items-center justify-center group-hover:bg-blue-50 transition-colors">
                          <Calendar size={22} className="text-gray-400 group-hover:text-[#189FAA]" />
                        </div>
                        <div>
                          <div className="font-bold text-[#071a3d]">{presc.userName}</div>
                          <div className="text-xs text-gray-500 font-medium">{presc.clinic_Name}</div>
                          <div className="text-[10px] text-gray-400 font-bold uppercase mt-1">{dayjs(presc.consultationDate).format('DD MMM YYYY')}</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Tag color="cyan" className="m-0 rounded-lg px-2 border-none font-bold text-[10px]">{presc.drugPrescription?.length || 0} Medicines</Tag>
                        <ChevronRight size={18} className="text-gray-300 group-hover:text-[#189FAA] transition-colors" />
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <Empty description="No prescriptions found" />
              )}
            </div>
          )}

          <div className="mt-8">
            <Button
              block
              size="large"
              onClick={() => setIsPrescModalOpen(false)}
              className="h-14 rounded-2xl border-gray-100 text-gray-500 font-bold hover:bg-gray-50"
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      <style>
        {`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .custom-scrollbar::-webkit-scrollbar {
            width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
            background: #f1f5f9;
            border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
        }
        .medicine-modal .ant-input {
            border-radius: 12px;
            font-weight: 600;
        }
        .medicine-modal .ant-input:focus, .medicine-modal .ant-input-focused {
            border-color: #189FAA;
            box-shadow: 0 0 0 2px rgba(24, 159, 170, 0.1);
        }
        .medicine-modal .ant-picker {
            border-radius: 12px;
            border-color: #f3f4f6;
        }
        .medicine-modal .ant-picker:hover, .medicine-modal .ant-picker-focused {
            border-color: #189FAA;
        }
        `}
      </style>
    </div>
  );
};

// const Pill = ({ color, size }) => (
//   <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
//     <path d="m10.5 20.5 10-10a4.95 4.95 0 1 0-7-7l-10 10a4.95 4.95 0 1 0 7 7Z" /><path d="m8.5 8.5 7 7" />
//   </svg>
// );

export default MedicineReminder;
